import { useEffect, useMemo, useRef } from 'react'
import clsx from 'clsx'
import type { DailyPoint } from '@/db/queries'

interface Props {
  data: DailyPoint[]
  metric?: 'recovery' | 'strain' | 'sleep_performance'
  daysBack: number
  selectedDay?: string | null
  onSelectDay?: (day: string) => void
}

/**
 * GitHub-style heatmap. Rows = day of week (Sun..Sat), columns = weeks.
 * Color encodes the chosen metric. Cell size scales with the date range so
 * shorter views fill the container instead of leaving dead space.
 */
export default function CalendarHeatmap({
  data,
  metric = 'recovery',
  daysBack,
  selectedDay,
  onSelectDay,
}: Props) {
  const cells = useMemo(() => {
    const byDay = new Map(data.map((d) => [d.day, d]))
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const start = new Date(today.getTime() - daysBack * 86_400_000)
    // Snap start back to the previous Sunday for a clean grid.
    start.setDate(start.getDate() - start.getDay())

    const days: { day: string; date: Date; value: number | null }[] = []
    for (let d = new Date(start); d <= today; d = new Date(d.getTime() + 86_400_000)) {
      const iso = d.toISOString().slice(0, 10)
      const point = byDay.get(iso)
      days.push({
        day: iso,
        date: new Date(d),
        value: extract(point, metric),
      })
    }
    return days
  }, [data, metric, daysBack])

  const weeks = useMemo(() => {
    const out: typeof cells[] = []
    for (let i = 0; i < cells.length; i += 7) out.push(cells.slice(i, i + 7))
    return out
  }, [cells])

  const { cellSize, gap, dowGutter, fontSize } = sizingFor(daysBack)

  const monthLabels = useMemo(() => {
    const labels: { idx: number; label: string }[] = []
    let lastMonth = -1
    weeks.forEach((w, i) => {
      const first = w[0]
      if (!first) return
      const m = first.date.getMonth()
      if (m !== lastMonth) {
        labels.push({ idx: i, label: first.date.toLocaleString('en', { month: 'short' }) })
        lastMonth = m
      }
    })
    return labels
  }, [weeks])

  const dowLabels = ['', 'Mon', '', 'Wed', '', 'Fri', '']

  const scrollRef = useRef<HTMLDivElement>(null)

  // When the range changes (or first render), scroll to the right edge so the
  // most recent dates are visible. Otherwise long ranges default to showing
  // the oldest data, which can be empty for newer Whoop users.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    el.scrollLeft = el.scrollWidth
  }, [daysBack, weeks.length])

  return (
    <div ref={scrollRef} className="w-full overflow-x-auto">
      <div className="flex justify-center min-w-fit">
        <div>
          <div
            className="flex text-stone-500"
            style={{ paddingLeft: dowGutter, marginBottom: gap, fontSize }}
          >
            {weeks.map((_, i) => {
              const lbl = monthLabels.find((m) => m.idx === i)
              return (
                <div
                  key={i}
                  style={{ width: cellSize + gap }}
                  className="text-left"
                >
                  {lbl?.label ?? ''}
                </div>
              )
            })}
          </div>
          <div className="flex" style={{ gap }}>
            <div
              className="flex flex-col text-stone-500"
              style={{ marginRight: gap, fontSize, gap }}
            >
              {dowLabels.map((l, i) => (
                <div
                  key={i}
                  style={{ height: cellSize, lineHeight: `${cellSize}px`, width: dowGutter - gap }}
                >
                  {l}
                </div>
              ))}
            </div>
            {weeks.map((week, wi) => (
              <div key={wi} className="flex flex-col" style={{ gap }}>
                {Array.from({ length: 7 }, (_, di) => {
                  const cell = week[di]
                  if (!cell)
                    return (
                      <div
                        key={di}
                        style={{ width: cellSize, height: cellSize }}
                        className="opacity-0"
                      />
                    )
                  const isSelected = cell.day === selectedDay
                  const inFuture = cell.date > new Date()
                  return (
                    <button
                      key={di}
                      type="button"
                      title={`${cell.day}: ${
                        cell.value == null ? 'no data' : Math.round(cell.value)
                      }`}
                      onClick={() => !inFuture && onSelectDay?.(cell.day)}
                      disabled={inFuture}
                      className={clsx(
                        'rounded-[3px] transition-all',
                        colorClass(cell.value, metric),
                        isSelected && 'ring-2 ring-offset-1 ring-offset-white ring-stone-900',
                        !inFuture && 'cursor-pointer hover:ring-1 hover:ring-stone-500',
                        inFuture && 'opacity-30 cursor-default',
                      )}
                      style={{ width: cellSize, height: cellSize }}
                    />
                  )
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function sizingFor(daysBack: number): {
  cellSize: number
  gap: number
  dowGutter: number
  fontSize: number
} {
  // Tuned so 90D fills ~600px wide, 1Y is comfortably readable, multi-year
  // remains compact and scrolls on narrow viewports.
  if (daysBack <= 90) return { cellSize: 36, gap: 5, dowGutter: 36, fontSize: 11 }
  if (daysBack <= 180) return { cellSize: 26, gap: 4, dowGutter: 30, fontSize: 11 }
  if (daysBack <= 365) return { cellSize: 16, gap: 3, dowGutter: 28, fontSize: 11 }
  if (daysBack <= 730) return { cellSize: 11, gap: 2, dowGutter: 24, fontSize: 10 }
  return { cellSize: 8, gap: 1, dowGutter: 22, fontSize: 10 }
}

function extract(p: DailyPoint | undefined, metric: Props['metric']): number | null {
  if (!p) return null
  switch (metric) {
    case 'recovery':
      return p.recovery ?? null
    case 'strain':
      return p.strain ?? null
    case 'sleep_performance':
      return p.sleep_performance ?? null
    default:
      return null
  }
}

function colorClass(v: number | null, metric: Props['metric']): string {
  if (v == null) return 'bg-stone-100 border border-stone-200'
  if (metric === 'strain') {
    // 0..21
    const t = Math.min(1, Math.max(0, v / 21))
    if (t < 0.2) return 'bg-sky-100'
    if (t < 0.4) return 'bg-sky-200'
    if (t < 0.6) return 'bg-sky-400'
    if (t < 0.8) return 'bg-sky-600'
    return 'bg-sky-800'
  }
  // recovery / sleep_performance: 0..100
  if (v >= 67) return 'bg-green-500'
  if (v >= 50) return 'bg-green-400'
  if (v >= 34) return 'bg-yellow-400'
  if (v >= 20) return 'bg-orange-400'
  return 'bg-red-400'
}
